/**
 * Helper de estilo visual pros status de clínica (`clinics.status`),
 * espelha o padrão de `lead-status-style.ts` — 4 níveis (milestone,
 * active, waiting, neutral, lost) traduzidos em badges/cards consistentes
 * em light + dark via tokens do DS.
 *
 * Status canônicos (alinhados com `CLINIC_STATUS_LABELS` em schemas.ts):
 *   - `active`     → milestone (cliente faturando ✅)
 *   - `trialing`   → active (em trial; ainda não convertido)
 *   - `paused`     → waiting (operações pausadas, recuperável)
 *   - `suspended`  → waiting (inadimplência ou ação manual)
 *   - `cancelled`  → lost (terminal)
 *
 * Status desconhecidos caem em `neutral` — UI nunca quebra com valor
 * inesperado vindo do banco.
 */

export type ClinicStatusLevel = 'milestone' | 'active' | 'waiting' | 'neutral' | 'lost';

export interface ClinicStatusStyle {
  level: ClinicStatusLevel;
  /** Classes pra aplicar no badge — mesma família de tokens dos
   * helpers de leads/proposals. */
  badgeClassName: string;
  /** Ícone que precede o label (emoji). */
  icon: string | null;
}

const LEVEL_STYLES: Record<ClinicStatusLevel, Omit<ClinicStatusStyle, 'level' | 'icon'>> = {
  milestone: {
    badgeClassName:
      'inline-flex items-center gap-1.5 rounded-md border border-accent/60 bg-accent/15 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-accent-foreground shadow-sm',
  },
  active: {
    badgeClassName:
      'inline-flex items-center gap-1 rounded-md border border-accent/40 bg-accent/5 px-2 py-0.5 text-xs font-medium text-accent-foreground',
  },
  waiting: {
    badgeClassName:
      'inline-flex items-center gap-1 rounded-md border border-warning/40 bg-warning/5 px-2 py-0.5 text-xs font-medium text-foreground',
  },
  neutral: {
    badgeClassName:
      'inline-flex items-center gap-1 rounded-md border border-border bg-muted/50 px-2 py-0.5 text-xs font-medium text-muted-foreground',
  },
  lost: {
    badgeClassName:
      'inline-flex items-center gap-1 rounded-md border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive',
  },
};

const STATUS_TO_LEVEL: Record<string, { level: ClinicStatusLevel; icon: string | null }> = {
  active: { level: 'milestone', icon: '🎉' },
  trialing: { level: 'active', icon: '⏳' },
  paused: { level: 'waiting', icon: '⏸️' },
  suspended: { level: 'waiting', icon: '⚠️' },
  cancelled: { level: 'lost', icon: '×' },
};

/** Resolve estilo visual pra um status canônico (case-insensitive +
 * fallback `neutral` quando o status não é conhecido — UI nunca falha
 * por valor inesperado). */
export function getClinicStatusStyle(status: string | null | undefined): ClinicStatusStyle {
  const norm = (status ?? '').toLowerCase();
  const mapped = STATUS_TO_LEVEL[norm] ?? { level: 'neutral' as const, icon: null };
  return {
    level: mapped.level,
    icon: mapped.icon,
    ...LEVEL_STYLES[mapped.level],
  };
}
